import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useState } from "react";

import type { TestShackReportInspection } from "@/pages/products/test-shack/models/TestShackReport";
import { TestShackReportImageDialog as SharedImageDialog } from "@/pages/products/test-shack/shared/components/report/TestShackReportImageDialog";

export const IMAGE_KIND = {
	DIMENSIONS: "dimensions",
	MILL_DRAWING: "millDrawing",
} as const;

export type ImageKind = (typeof IMAGE_KIND)[keyof typeof IMAGE_KIND];

interface TestShackReportImageDialogProps {
	inspection: TestShackReportInspection;
	initialKind?: ImageKind;
	onClose: () => void;
}

export const TestShackReportImageDialog = ({
	inspection,
	initialKind,
	onClose,
}: TestShackReportImageDialogProps) => {
	const hasDimensions = inspection.DimensionsReferenceUploadFileId !== null;
	const hasMillDrawing = inspection.MillDrawingUploadFileId !== null;
	const [selectedKind, setSelectedKind] = useState<ImageKind>(() => {
		if (initialKind === IMAGE_KIND.MILL_DRAWING && hasMillDrawing)
			return IMAGE_KIND.MILL_DRAWING;
		if (initialKind === IMAGE_KIND.DIMENSIONS && hasDimensions)
			return IMAGE_KIND.DIMENSIONS;
		return hasMillDrawing ? IMAGE_KIND.MILL_DRAWING : IMAGE_KIND.DIMENSIONS;
	});
	const selectedFileId =
		selectedKind === IMAGE_KIND.DIMENSIONS
			? inspection.DimensionsReferenceUploadFileId
			: inspection.MillDrawingUploadFileId;
	const selectedLabel =
		selectedKind === IMAGE_KIND.DIMENSIONS
			? "Dimensions reference"
			: "Mill drawing";

	return (
		<SharedImageDialog
			sectionName={inspection.SectionName}
			uploadFileId={selectedFileId}
			label={selectedLabel}
			onClose={onClose}
		>
			{hasDimensions && hasMillDrawing && (
				<ToggleButtonGroup
					exclusive
					size="small"
					value={selectedKind}
					onChange={(_event, value: ImageKind | null) => {
						if (value !== null) setSelectedKind(value);
					}}
					aria-label="Historical image type"
				>
					<ToggleButton value={IMAGE_KIND.DIMENSIONS}>
						Dimensions reference
					</ToggleButton>
					<ToggleButton value={IMAGE_KIND.MILL_DRAWING}>
						Mill drawing
					</ToggleButton>
				</ToggleButtonGroup>
			)}
		</SharedImageDialog>
	);
};
